/* eslint-disable no-param-reassign */
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState,
  useMemo
} from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import SvgIcon from '@mui/material/SvgIcon';
import {Button, Typography} from '@customink/pigment-react';
import Cookies from 'js-cookie';
import Dropzone, {OnDragFilesParams, FileRejection} from '../Dropzone';
import {
  useDirectUpload,
  useAppDispatch,
  useAppSelector,
  usePrevious
} from '../../hooks';
import {
  addPendingDetail,
  addLocalImage,
  removePendingDetail
} from '../../slices/storesSlice';
import {StoreImageType} from '../../models/storeDetails';
import services from '../../api/remoteServices';
import CameraIcon from '../../assets/icons/camera.svg?com';
import './index.scss';

interface ImageUploaderProps {
  storeImageType: StoreImageType;
  onBlobUpload?: (blob: ActiveStorage.Blob) => void;
  onCancel?: () => void;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  storeImageType,
  onBlobUpload,
  onCancel
}: ImageUploaderProps): ReactElement => {
  const dispatch = useAppDispatch();
  const localImages = useAppSelector(state => state.stores.localImages);
  const [fileName, setFileName] = useState('');
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [draggedFileCount, setDraggedFileCount] = useState<number>(0);
  const [filesRejected, setFilesRejected] = useState<boolean>(false);
  const {
    uploadFile,
    uploadStatus,
    uploadProgress,
    setXhrHandler,
    blob,
    error,
    resetUpload
  } = useDirectUpload();
  const previousLocalImage = usePrevious(localImages[storeImageType]);
  const previousBlob = usePrevious(blob);

  useEffect(() => {
    if (!localImages[storeImageType] && !!previousLocalImage) resetUpload();
  }, [localImages]);

  useEffect(() => {
    if (blob && !previousBlob) {
      dispatch(addPendingDetail({key: storeImageType, val: blob.signed_id}));
      if (onBlobUpload) {
        onBlobUpload(blob);
      }
    }
  }, [blob, onBlobUpload, dispatch]);

  const onUploadCancel = useCallback(() => {
    resetUpload();
    dispatch(removePendingDetail(storeImageType));
    if (onCancel) {
      onCancel();
    }
  }, [resetUpload, dispatch, onCancel]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]): void => {
      onUploadCancel();
      if (rejectedFiles.length) {
        setFilesRejected(true);
      } else if (acceptedFiles.length) {
        dispatch(
          addLocalImage({file: acceptedFiles[0], imageType: storeImageType})
        );
        setFileName(acceptedFiles[0].name);
        setXhrHandler((xhr: XMLHttpRequest) => {
          xhr.setRequestHeader(
            'X-CSRF-Token',
            decodeURIComponent(Cookies.get('stores_csrf'))
          );
          xhr.withCredentials = true;
        });
        const fileDate = new Date()
          .toISOString()
          .slice(2, 10)
          .replace(/-/g, ''); // YYMMDD
        const genericFileName = `${storeImageType}Upload${fileDate}`;
        const genericFile = new File([acceptedFiles[0]], genericFileName, {
          type: acceptedFiles[0].type
        });
        uploadFile(
          genericFile,
          `${services.stores}/rails/active_storage/direct_uploads`
        );
        setFilesRejected(false);
      }
    },
    [uploadFile, setFileName, setXhrHandler, setFilesRejected, onUploadCancel]
  );

  const onDragFile = useCallback(
    ({draggedFiles, isDragActive}: OnDragFilesParams): void => {
      setFilesRejected(false);
      setDragActive(isDragActive);
      setDraggedFileCount(draggedFiles.length);
    },
    [setDragActive, setDraggedFileCount, setFilesRejected]
  );

  const uploadFooter = useMemo(() => {
    let content = <></>;
    if (uploadStatus === 'inProcess' || uploadStatus === 'complete')
      content = (
        <>
          <Typography noWrap>{fileName}</Typography>
          <LinearProgress
            variant="determinate"
            color="success"
            value={uploadProgress * 100}
          />
          <Button
            onClick={onUploadCancel}
            className="imageUploaderCancelButton"
            variant="link">
            Cancel
          </Button>
        </>
      );
    else if (error) content = <Typography>{error.message}</Typography>;
    return (
      <div
        className={`imageUploaderFooter${
          filesRejected ? ' rejectedFiles' : ''
        }`}>
        {content}
      </div>
    );
  }, [
    uploadStatus,
    fileName,
    uploadProgress,
    error,
    filesRejected,
    blob,
    onUploadCancel
  ]);

  const dragText = useMemo(() => {
    if (dragActive) {
      if (draggedFileCount > 1) return 'Multiple files not supported';
      if (draggedFileCount === 1) return 'Drop Image';
    }
    return 'Upload Image';
  }, [dragActive, draggedFileCount]);

  const dragClass: string = useMemo(() => {
    if (dragActive)
      return `dragActive ${draggedFileCount > 1 ? ' dragRejected' : ''}`;
    return '';
  }, [dragActive, draggedFileCount]);

  return (
    <>
      <Dropzone
        maxFiles={1}
        multiple={false}
        maxSize={5000000}
        accept={['image/jpeg', 'image/png']}
        onDrop={onDrop}
        onDragFile={onDragFile}
        className={dragClass}>
        <>
          {draggedFileCount <= 1 && (
            <SvgIcon
              viewBox="0 0 22 19"
              color="inherit"
              component={CameraIcon}
            />
          )}
          <Typography variant="captionLarge" className="dropzoneLink">
            {dragText}
          </Typography>
        </>
      </Dropzone>
      {uploadFooter}
    </>
  );
};

ImageUploader.defaultProps = {
  onBlobUpload: undefined,
  onCancel: undefined
};

export default ImageUploader;
